import { configureStore, createListenerMiddleware, isAction } from '@reduxjs/toolkit';
import authReducer from '@/features/auth/authSlice';
import workspaceReducer, {
  setCurrentWorkspace,
  switchWorkspace,
} from '@/features/workspace/workspaceSlice';
import webinarReducer from '@/features/webinar/webinarSlice';
import liveReducer from '@/features/live/liveSlice';
import dashboardReducer from '@/features/dashboard/store/dashboardSlice.ts';
import analyticsReducer from '@/features/analytics/store/analyticsSlice';
import billingReducer from '@/features/billing/billingSlice';
import { fetchSubscription, fetchUsage } from '@/features/billing/billingThunks';
import {
  fetchUserWorkspaces,
  fetchWorkspaceById,
  fetchWorkspaceDetails,
} from '@/features/workspace/workspaceThunks';

const billingListener = createListenerMiddleware();
billingListener.startListening({
  predicate: (action) =>
    fetchUserWorkspaces.fulfilled.match(action) ||
    setCurrentWorkspace.match(action) ||
    switchWorkspace.match(action) ||
    fetchWorkspaceById.fulfilled.match(action),
  effect: async (_action, listenerApi) => {
    const state = listenerApi.getState() as {
      workspace: { currentWorkspace: { id: number } | null };
    };
    const workspaceId = state.workspace.currentWorkspace?.id;
    if (workspaceId != null) {
      listenerApi.dispatch(fetchWorkspaceDetails(workspaceId));
      listenerApi.dispatch(fetchSubscription(workspaceId));
      listenerApi.dispatch(fetchUsage(workspaceId));
    }
  },
});

const loggerMiddleware = (store: any) => (next: any) => (action: any) => {
  if (isAction(action)) {
    console.group(`[Store] Action: ${action.type}`);
    if ('payload' in action) {
      console.log('Payload:', (action as any).payload);
    }
    const previousState = store.getState();
    console.log('Previous state:', previousState);

    const result = next(action);

    const nextState = store.getState();
    console.log('Next state:', nextState);
    console.groupEnd();

    return result;
  }
  return next(action);
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    workspace: workspaceReducer,
    webinar: webinarReducer,
    live: liveReducer,
    dashboard: dashboardReducer,
    analytics: analyticsReducer,
    billing: billingReducer,
  },
  middleware: (getDefaultMiddleware) => {
    const base = getDefaultMiddleware().prepend(billingListener.middleware);
    return import.meta.env.VITE_ENVIRONMENT === 'production' ? base : base.concat(loggerMiddleware);
  },
});
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
